/**
 * empStatus.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source-of-truth for employee status logic (UI only — no data changes).
 *
 * Rules:
 *  1. On OD                           → "od"
 *  2. Currently checked in            → "present"
 *  3. Has checkOutTime today          → "checked-out"  (checked in & left, incl. 7 PM auto)
 *  4. Manager-set Absent (no check-in)→ "absent"
 *  5. No check-in AND time ≥ 10:00 AM IST → "absent"   (missed the 10 AM deadline)
 *  6. No check-in AND time < 10:00 AM IST → "pending"  (still early, may come in)
 */

/**
 * Returns the current IST hour (0-23).
 */
function getISTHour() {
  return parseInt(
    new Date().toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      hour12: false,
    }),
    10
  );
}

function todayIstYmd() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function ymd(value) {
  if (value == null || value === "") return "";
  const s = String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s;
}

export function attendanceStatusToday(emp) {
  const today = todayIstYmd();
  const acc = emp.attendance || {};
  return String(acc[today] || acc[ymd(today)] || "").toLowerCase();
}

export function hasCheckInToday(emp) {
  if (emp.checkedIn || emp.checked_in) return true;
  const today = todayIstYmd();
  return (emp.checkinsHistory || []).some((c) => c.type === "in" && ymd(c.date) === today);
}

export function isOnLeaveToday(emp) {
  const att = attendanceStatusToday(emp);
  return att === "leave" || att === "cl" || att === "ml";
}

export function isOnLopToday(emp) {
  return attendanceStatusToday(emp) === "lop";
}

/** True when the employee has no check-in today and can be marked Absent. */
export function canMarkAbsent(emp) {
  if (!emp) return false;
  if ((emp.role || "").toLowerCase() === "manager") return false;
  if (emp.is_disabled) return false;
  if (emp.onOD || emp.on_od) return false;
  if (emp.canMarkAbsent === false) return false;
  if (hasCheckInToday(emp)) return false;
  if (isOnLeaveToday(emp) || isOnLopToday(emp)) return false;
  return true;
}

export function isMarkedAbsentToday(emp) {
  return attendanceStatusToday(emp) === "absent" && !hasCheckInToday(emp);
}

/**
 * Derives the display status for a single employee object.
 *
 * @param {object} emp  - Employee object from AppContext (team array).
 * @returns {{ status: string, checkOutTime: string|null }}
 *   status: one of "od" | "present" | "checked-out" | "absent" | "pending"
 *   checkOutTime: the checkout time string (only set when status === "checked-out")
 */
export function getEmpStatus(emp) {
  if (emp.onOD) {
    return { status: "od", checkOutTime: null };
  }
  if (emp.checkedIn) {
    return { status: "present", checkOutTime: null };
  }
  if (emp.checkOutTime) {
    // Checked in at some point today and then checked out (manual or auto 7 PM)
    return { status: "checked-out", checkOutTime: emp.checkOutTime };
  }
  if (isOnLeaveToday(emp)) {
    return { status: "leave", checkOutTime: null };
  }
  if (isOnLopToday(emp)) {
    return { status: "lop", checkOutTime: null };
  }
  if (isMarkedAbsentToday(emp) || getISTHour() >= 10) {
    return { status: "absent", checkOutTime: null };
  }
  // Before 10 AM — not yet late, still pending
  return { status: "pending-arrival", checkOutTime: null };
}

/**
 * Returns true if the employee is considered "absent" today
 * (manager-marked, or never checked in and it is past 10:00 AM IST).
 */
export function isEmpAbsent(emp) {
  if (!emp) return false;
  if ((emp.role || "").toLowerCase() === "manager") return false;
  if (emp.is_disabled) return false;
  if (emp.checkedIn || emp.onOD || emp.checkOutTime || hasCheckInToday(emp)) return false;
  if (isOnLeaveToday(emp) || isOnLopToday(emp)) return false;
  if (isMarkedAbsentToday(emp)) return true;
  return getISTHour() >= 10;
}
